import React, {
  useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Dialog, DialogActions, DialogContent, DialogContentText, Snackbar, Alert,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {
  CardWrapper, ButtonUI, ButtonOutlined, Field,
} from '../../UI';
import IdentificacaoDevedor from '../Devedor/IdentificacaoDevedor';
import EnderecoDevedor from '../Devedor/EnderecoDevedor';
import TelefoneDevedor from '../Devedor/TelefoneDevedor';
import EmailDevedor from '../Devedor/EmailDevedor';
import DadosAdvogado from '../Devedor/DadosAdvogado';
import DadosSocios from '../Devedor/DadosSocios';
import TableDevedorGrupo from '../Devedor/TableDevedorGrupo';
import {
  mapearDevedorParaFormFields, buildEnderecoVazio, buildTelefoneVazio, buildEmailVazio,
  montarPayloadDevedor, makeErrorFieldsDevedor,
} from '../Devedor/devedorController';
import usePodeGerenciarDevedor from '../../../hooks/usePodeGerenciarDevedor';
import usePodePesquisarWebServiceDevedor from '../../../hooks/usePodePesquisarWebServiceDevedor';
import useListaComPrincipal from '../../../hooks/useListaComPrincipal';
import { buscarDevedorGrupo } from '../../../../services/core/processos/processosService';
import {
  atualizarDadosDevedor, constultarDadosComplementaresDevedorWebserviceExterno,
} from '../../../../services/core/processos/processosDevedorService';
import { isBadRequestException, haveExceptionValidations } from '../../../../helpers/utils/utils';
import { updateFieldsWithErrors, extractResponseErrorMessage } from '../../../../helpers/utils/form.utils';
import * as C from '../../../theme/colors.constants';

/**
 * Aba Devedor — print `grid_check_devedor.png`.
 *
 * Cabeçalho com ícone + título + os botões "PESQUISA WEB SERVICE" e (AC1)
 * "Atualizar Cadastro"/"Cancelar"+"Salvar" (troca por `isEditMode`, mesmo
 * padrão de UM único par de controles alternando — nunca os dois pares
 * simultâneos — já usado na tela real `DadosDevedor.jsx`).
 *
 * AW2-1134 Etapa 2: modo de edição real. `formFields` (estado local, plano —
 * sem `FieldModel`, stack isolada do protótipo) é a ÚNICA fonte de exibição
 * dos campos de `IdentificacaoDevedor.jsx`, sincronizado com `dadosDevedor`
 * via efeito (mesmo padrão de `formFields`/`useEffect` da tela real). O
 * botão "Salvar" fica visível mas `disabled` nesta etapa — a persistência
 * real (`atualizarDadosDevedor`) é a Etapa 9 do plano; deixá-lo inerte em vez
 * de fingir sucesso evita uma UI enganosa que parece salvar sem salvar de
 * verdade.
 *
 * O par "CANCELAR"/"GRAVAR" que existia solto no rodapé do mock (duplicando
 * a mesma função do par do cabeçalho) foi removido nesta etapa — a própria
 * issue Jira (AW2-1134) pede explicitamente "sem duplicidade de funções
 * equivalentes". Só o par do cabeçalho é funcional.
 *
 * AW2-1134 Etapa 3: `formFields.enderecos` (lista real, múltiplos itens)
 * substitui `BlocoEnderecos` mockado — `EnderecoDevedor.jsx` novo cobre
 * add/remove/"Principal" mutuamente exclusivo, mesmas regras de
 * `EnderecosV2/Enderecos.jsx` real.
 *
 * AW2-1134 Etapa 4: `formFields.telefones` substitui `BlocoTelefones`
 * mockado — `TelefoneDevedor.jsx` novo, mesmo padrão de Endereços mais
 * ícone de copiar (`exibirBotaoCopiar`, só `podeModificarFase`) e WhatsApp
 * não-exclusivo (diferente de "Principal").
 *
 * AW2-1134 Etapa 5: `formFields.emails` (componente novo, sem mock
 * anterior) — `EmailDevedor.jsx`. 3ª ocorrência do trio de handlers
 * add/remover/marcar-principal (Endereços/Telefones/E-mails) extraída para
 * `hooks/useListaComPrincipal.js` — Endereços/Telefones retrofitados para o
 * mesmo hook nesta etapa, mesmo comportamento, regra "2x aceitável, 3ª
 * extrai" já em uso no módulo.
 *
 * AW2-1134 Etapa 6: `DadosAdvogado.jsx` novo, 2 campos simples
 * (`nomeAdvogado`/`telefoneAdvogado`) ligados ao `onChangeCampo` genérico já
 * usado por `IdentificacaoDevedor.jsx` — sem lista, sem mapeamento especial.
 * Posicionado depois de `EmailDevedor` e antes de `DadosSocios` (Etapa 7),
 * mesma ordem de `DadosDevedor.jsx` real.
 *
 * AW2-1134 Etapa 7: `DadosSocios.jsx` novo — tabela somente-leitura
 * (Nome/CPF), sem `if` de PF/PJ (renderizada incondicionalmente, PF sempre
 * resulta em 0 linhas via `formFields.socios` vazio). Sem modo de edição —
 * não recebe `isEditMode`/`podeModificarFase`.
 *
 * AW2-1134 Etapa 8: campo "Cód. Devedor principal:" ligado a
 * `formFields.codDevedorPrincipal` (real) + `TableDevedorGrupo.jsx` novo,
 * populada por `buscarDevedorGrupo` — remove `ANTECEDENTES_DEVEDOR` mockado.
 * Gate do campo replica DELIBERADAMENTE o bug real (`disabled={!isEditMode}`,
 * sem `podeModificarFase`) — decisão de paridade exata confirmada com o
 * usuário no planejamento, não "corrigir" numa revisão futura sem
 * rediscutir. `buscarDevedorGrupo` importado direto do service (não via
 * saga) dentro do componente — mesmo padrão já usado pela produção real
 * (`DadosDevedor.jsx` importa o service direto, não recebe por prop) e
 * consistente com o precedente já estabelecido no protótipo de chamar
 * services direto de `ControleProcessosPage.jsx`; aqui o fetch fica no
 * componente porque é estado de UI local a esta aba (mesmo raciocínio já
 * usado para os handlers de Endereço/Telefone/E-mail), não dado do
 * processo como um todo.
 *
 * AW2-1134 Etapa 9: "Salvar" real. `idProcesso`/`onDevedorMutado` voltam a
 * ser props (removidas por não uso nas Etapas 0-1) — `onDevedorMutado` é o
 * refetch de `dadosDevedor` em `ControleProcessosPage.jsx`, mesmo papel de
 * `updatePage()` real. Dialog de confirmação local (MUI v5, não
 * `OptionDialogNew` de produção), mesmo texto de aviso. Guard de sessão
 * (`sessaoRef`/`enviandoRef`, incrementado só quando `idProcesso`
 * REALMENTE muda — não a cada refetch de `dadosDevedor` do mesmo processo)
 * replica o padrão de `BlocoAnotacao.jsx`, não `useSessaoDialogMutacao`
 * (esse hook é para dialogs com ciclo de vida `open`/`close`; esta aba fica
 * sempre montada). Erro de validação (400 com `validations`) mapeado por
 * campo só para os campos simples de Identificação/Advogado
 * (`errorFields`, `devedorController.makeErrorFieldsDevedor`) — Endereços/
 * Telefones/E-mails/Sócios não recebem erro por item nesta etapa (mesmo
 * limite observável do `onSubmitHandler` real, que também só mostra um
 * toast por bloco, não por item da lista); qualquer validação fora dos
 * campos simples cai num toast genérico agregado, mesmo padrão de
 * `naoMapeados` já usado em `DialogAnaliseSimulacao.jsx`.
 *
 * AW2-1134 Etapa 10: botão "PESQUISA WEB SERVICE" real —
 * `disabled={!dadosDevedor?.cnpjCpf || !podePesquisarWebService}`, mesma
 * condição de produção, INDEPENDENTE de `isEditMode` (o botão fica ativo
 * mesmo em modo leitura, diferente de todo o resto da aba). Gate lê
 * `dadosDevedor?.cnpjCpf` (o dado PERSISTIDO), não `formFields.cnpjCpf` (o
 * rascunho em edição) — mesma leitura de `devedor?.cnpjCpf` real.
 * `onClickPesquisarWebService` reaproveita o mesmo `sessaoRef` da Etapa 9
 * (guard de sessão compartilhado entre as 2 mutações desta aba), mas tem
 * seu próprio `pesquisandoWebServiceRef`/`pesquisandoWebService` e
 * `erro`/`sucessoWebService` — estado SEPARADO do de "Salvar" porque são 2
 * ações independentes que podem, em tese, estar em voo ao mesmo tempo
 * (compartilhar o mesmo Snackbar/flag misturaria o feedback de uma ação
 * com o resultado da outra). `finally` chama `onDevedorMutado()`
 * INCONDICIONALMENTE (mesmo fora da sessão alvo) — mesmo padrão do
 * `finally` real (`updatePage()` sempre) — seguro porque o refetch em si já
 * tem seu próprio guard (`idProcessoRef`) em `ControleProcessosPage.jsx`.
 */
export default function AbaDevedor({
  dadosDevedor, podeModificarFase, idProcesso, onDevedorMutado,
}) {
  const podeGerenciarDevedor = usePodeGerenciarDevedor();
  const podePesquisarWebService = usePodePesquisarWebServiceDevedor();
  const categoriasOptions = useSelector(state => state.selectors.categorias.selector);
  const estadosOptions = useSelector(state => state.selectors.estados.selector);
  const tiposTelefoneOptions = useSelector(state => state.selectors.tiposTelefone.selector);

  const [isEditMode, setIsEditMode] = useState(false);
  const [formFields, setFormFields] = useState(() => mapearDevedorParaFormFields(dadosDevedor));
  const [devedoresDoGrupo, setDevedoresDoGrupo] = useState([]);
  const [erroCodDevedorPrincipal, setErroCodDevedorPrincipal] = useState('');
  const [showSaveAlert, setShowSaveAlert] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [errorFields, setErrorFields] = useState(() => makeErrorFieldsDevedor());
  const [erroSalvar, setErroSalvar] = useState('');
  const [sucessoSalvar, setSucessoSalvar] = useState('');
  const [pesquisandoWebService, setPesquisandoWebService] = useState(false);
  const [erroWebService, setErroWebService] = useState('');
  const [sucessoWebService, setSucessoWebService] = useState('');

  // Guard de sessão para o submit real (Etapa 9) — mesmo padrão de
  // `sessaoRef`/`enviandoRef` de `BlocoAnotacao.jsx`, não
  // `useSessaoDialogMutacao` (esse hook reage a `open`/`close` de dialog;
  // esta aba fica sempre montada, só o formulário alterna `isEditMode`).
  // `idProcessoAnteriorRef` incrementa `sessaoRef` só quando `idProcesso`
  // REALMENTE muda — uma comparação simples "idProcesso ainda é o mesmo"
  // não basta (cenário A→B→A: a resposta tardia de um save em A que ainda
  // está em voo quando o usuário volta a A não pode aplicar efeito sobre o
  // formulário já resetado/redigitado dessa 2ª visita a A).
  const sessaoRef = useRef(0);
  const enviandoRef = useRef(false);
  const pesquisandoWebServiceRef = useRef(false);
  const idProcessoAnteriorRef = useRef(idProcesso);
  useEffect(() => {
    if (idProcessoAnteriorRef.current !== idProcesso) {
      sessaoRef.current += 1;
      idProcessoAnteriorRef.current = idProcesso;
      enviandoRef.current = false;
      setEnviando(false);
      setShowSaveAlert(false);
      setErrorFields(makeErrorFieldsDevedor());
      setErroSalvar('');
      setSucessoSalvar('');
      pesquisandoWebServiceRef.current = false;
      setPesquisandoWebService(false);
      setErroWebService('');
      setSucessoWebService('');
    }
  }, [idProcesso]);

  // Reseta `isEditMode` junto com `formFields` sempre que `dadosDevedor`
  // mudar — não só o dado do fetch (achado em code review): esta aba nunca
  // desmonta ao trocar de processo (só a prop `dadosDevedor` muda), então
  // sem isso o modo de edição de um processo A "vazaria" para a tela do
  // processo B, liberando os 7 campos editáveis sem o usuário ter clicado
  // "Atualizar Cadastro" para B. Mesma categoria de bug já documentada 2x em
  // `src/prototipo/CLAUDE.md` para este container ("estado de UI... também
  // precisa resetar ao trocar idProcesso, não só o dado do fetch").
  // `devedoresDoGrupo`/`erroCodDevedorPrincipal` (Etapa 8) são a mesma
  // categoria — resultado de uma busca disparada pela digitação do usuário,
  // sem relação com `dadosDevedor` do processo novo.
  useEffect(() => {
    setIsEditMode(false);
    setFormFields(mapearDevedorParaFormFields(dadosDevedor));
    setDevedoresDoGrupo([]);
    setErroCodDevedorPrincipal('');
    setErrorFields(makeErrorFieldsDevedor());
  }, [dadosDevedor]);

  // Busca disparada pela mudança do campo "Cód. Devedor principal:" — cobre
  // tanto a digitação do usuário quanto o valor já vir populado do DTO ao
  // trocar de processo (a troca de `dadosDevedor` acima já reseta
  // `formFields`, incluindo `codDevedorPrincipal`, então este efeito reage
  // à mudança de valor de qualquer origem, mesmo comportamento da produção
  // real onde o efeito equivalente roda tanto no mount quanto a cada
  // digitação). Guard `ignore` (mesmo padrão já documentado no módulo para
  // fetches disparados por digitação) evita que uma resposta antiga
  // sobrescreva o resultado de uma busca mais recente.
  useEffect(() => {
    if (!formFields.codDevedorPrincipal) {
      setErroCodDevedorPrincipal('');
      return undefined;
    }

    let ignore = false;
    buscarDevedorGrupo(formFields.codDevedorPrincipal, formFields.idDevedor)
      .then((response) => {
        if (ignore) {
          return;
        }
        const resultado = response.data || [];
        setDevedoresDoGrupo(resultado);
        // Divergência deliberada da produção real: `getDevedorGrupoHandler`
        // real, para 0 resultados, marca só `codDevedorPrincipal.error =
        // true` sem nenhum texto em `errorMessageCodDevedor` (borda vermelha
        // muda, sem mensagem nenhuma — indistinguível de qualquer outro
        // estado à primeira vista). Aqui uma mensagem explícita substitui o
        // silêncio — não pedido pelo plano desta etapa (que só pede
        // paridade exata para o gate `disabled`, não para o texto de erro),
        // e não altera nenhum contrato de payload/API.
        setErroCodDevedorPrincipal(resultado.length === 0 ? 'Devedor não encontrado' : '');
      })
      .catch((erro) => {
        if (ignore) {
          return;
        }
        setDevedoresDoGrupo([]);
        setErroCodDevedorPrincipal(erro?.response?.data?.message || 'Erro ao buscar devedor');
      });

    return () => { ignore = true; };
  }, [formFields.codDevedorPrincipal, formFields.idDevedor]);

  const onChangeCampo = (e) => {
    const { name, value } = e.target;
    setFormFields(atual => ({ ...atual, [name]: value }));
  };

  // 3ª ocorrência do trio de handlers de array (add/remover/marcar
  // principal) sobre `formFields.*` (Endereços Etapa 3, Telefones Etapa 4,
  // agora E-mails) — extraído para `useListaComPrincipal` nesta etapa,
  // regra "2x aceitável, 3ª extrai" já em uso no módulo. Endereços/Telefones
  // retrofitados para o mesmo hook, mesmo comportamento, sem duplicar a
  // lógica pela 3ª vez.
  const enderecosHandlers = useListaComPrincipal(setFormFields, 'enderecos', buildEnderecoVazio);
  const telefonesHandlers = useListaComPrincipal(setFormFields, 'telefones', buildTelefoneVazio);
  const emailsHandlers = useListaComPrincipal(setFormFields, 'emails', buildEmailVazio);

  // Guard duplicado no handler, não só no `disabled` do botão — defesa em
  // profundidade já convencionada no módulo para todo gate de permissão/fase
  // (ver `usePodeAtuarNaFase`, `src/prototipo/CLAUDE.md`).
  const onClickEditar = () => {
    if (!podeModificarFase || !podeGerenciarDevedor) {
      return;
    }
    setIsEditMode(true);
  };

  const onClickCancelar = () => {
    setIsEditMode(false);
    setFormFields(mapearDevedorParaFormFields(dadosDevedor));
    setErrorFields(makeErrorFieldsDevedor());
  };

  const onClickSalvar = () => {
    setShowSaveAlert(true);
  };

  // `closeAlert` real: "Cancelar" no dialog de confirmação não só fecha o
  // dialog — sai do modo de edição inteiro e descarta o rascunho
  // (`setIsEditMode(false)` + `setFormFields(makeFormFields(...))`), mesmo
  // comportamento de `onClickCancelar` do cabeçalho. Confirmado por leitura
  // direta (`OptionDialogNew onClickCancel={closeAlert}` real) — não um
  // "fechar só o dialog" como pareceria mais natural à primeira vista.
  const onCancelarSalvar = () => {
    if (enviandoRef.current) {
      return;
    }
    setShowSaveAlert(false);
    setIsEditMode(false);
    setFormFields(mapearDevedorParaFormFields(dadosDevedor));
    setErrorFields(makeErrorFieldsDevedor());
  };

  // `onClickConfirm={onSubmitHandler}` real dispara direto do botão
  // "Confirmar" do dialog — mesmo formato aqui, sem uma etapa extra de
  // confirmação-da-confirmação.
  const onConfirmarSalvar = async () => {
    if (enviandoRef.current) {
      return;
    }

    const sessaoAlvo = sessaoRef.current;
    const payload = montarPayloadDevedor(formFields, categoriasOptions, idProcesso);
    enviandoRef.current = true;
    setEnviando(true);

    try {
      await atualizarDadosDevedor(idProcesso, payload);
      if (sessaoRef.current === sessaoAlvo) {
        setIsEditMode(false);
        setShowSaveAlert(false);
        setErrorFields(makeErrorFieldsDevedor());
        setSucessoSalvar('Ação realizada com sucesso!');
        if (onDevedorMutado) {
          onDevedorMutado();
        }
      }
    } catch (exception) {
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      // Fecha o dialog mesmo em erro (mesmo `finally` incondicional de
      // `onSubmitHandler` real) — deixá-lo aberto esconderia atrás do
      // backdrop o próprio campo com erro que acabou de ser marcado.
      setShowSaveAlert(false);
      if (isBadRequestException(exception) && haveExceptionValidations(exception)) {
        // `updateFieldsWithErrors`/`setErrorsInFields` (porta de produção)
        // indexa `fields[nome][indice]` para paths como `enderecos[0].cep`
        // — quebraria aqui porque `errorFields` só cobre campos simples
        // (decisão documentada em `makeErrorFieldsDevedor`), não arrays.
        // `try/catch` defensivo evita que uma validação inesperada do
        // backend quebre o fluxo de erro inteiro; cai no fallback genérico.
        try {
          const [atualizados, naoMapeados] = updateFieldsWithErrors(exception, makeErrorFieldsDevedor());
          const temErroVisivel = Object.values(atualizados).some(campo => campo.error);
          if (temErroVisivel) {
            setErrorFields(atualizados);
          }
          if (naoMapeados.length > 0) {
            setErroSalvar(naoMapeados.map(item => item.message).join(' '));
          } else if (!temErroVisivel) {
            setErroSalvar(extractResponseErrorMessage(exception));
          }
        } catch {
          setErroSalvar(extractResponseErrorMessage(exception));
        }
      } else {
        setErroSalvar(extractResponseErrorMessage(exception));
      }
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
        setEnviando(false);
      }
    }
  };

  const onFecharErroSalvar = () => setErroSalvar('');
  const onFecharSucessoSalvar = () => setSucessoSalvar('');

  // `onClickPesquisarWebService` real: guard de sessão compartilhado
  // (`sessaoRef`), mas double-click próprio (`pesquisandoWebServiceRef`,
  // independente de `enviandoRef` do "Salvar" — são 2 mutações distintas).
  // `extractResponseErrorMessage` (não a leitura direta
  // `e.response.data.message`/`.error` real, sem guarda) evita o mesmo
  // fail-open já corrigido em `DialogAnaliseSimulacao.jsx`: a produção
  // acessa `e.response.data` sem optional chaining, o que lançaria um novo
  // `TypeError` dentro do próprio `catch` para um erro de rede (sem
  // `e.response`), quebrando a promise silenciosamente.
  const onClickPesquisarWebService = async () => {
    if (pesquisandoWebServiceRef.current) {
      return;
    }

    const sessaoAlvo = sessaoRef.current;
    pesquisandoWebServiceRef.current = true;
    setPesquisandoWebService(true);

    try {
      await constultarDadosComplementaresDevedorWebserviceExterno(dadosDevedor?.cnpjCpf);
      if (sessaoRef.current === sessaoAlvo) {
        setSucessoWebService('Pesquisa realizada com sucesso');
      }
    } catch (exception) {
      if (sessaoRef.current === sessaoAlvo) {
        setErroWebService(extractResponseErrorMessage(exception));
      }
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        pesquisandoWebServiceRef.current = false;
        setPesquisandoWebService(false);
      }
      // Incondicional (mesmo fora da sessão alvo) — replica o `finally`
      // real (`updatePage()` sempre); seguro porque `onDevedorMutado` já
      // tem seu próprio guard (`idProcessoRef`) em `ControleProcessosPage.jsx`.
      if (onDevedorMutado) {
        onDevedorMutado();
      }
    }
  };

  const onFecharErroWebService = () => setErroWebService('');
  const onFecharSucessoWebService = () => setSucessoWebService('');

  return (
    <CardWrapper>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap',
      }}
      >
        <PersonOutlineIcon sx={{ color: C.navy }} />
        <Typography variant="h2" sx={{ flex: 1 }}>Dados do Devedor</Typography>
        <ButtonUI
          size="small"
          onClick={onClickPesquisarWebService}
          disabled={!dadosDevedor?.cnpjCpf || !podePesquisarWebService || pesquisandoWebService}
        >
          PESQUISA WEB SERVICE
        </ButtonUI>
        {isEditMode ? (
          <>
            <ButtonOutlined size="small" onClick={onClickCancelar} disabled={enviando}>Cancelar</ButtonOutlined>
            <ButtonUI size="small" onClick={onClickSalvar} disabled={enviando}>Salvar</ButtonUI>
          </>
        ) : (
          <ButtonUI
            size="small"
            disabled={!podeModificarFase || !podeGerenciarDevedor}
            onClick={onClickEditar}
          >
            Atualizar Cadastro
          </ButtonUI>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
          gap: 'clamp(10px, 0.8vw, 22px)',
          mb: 'clamp(10px, 0.8vw, 22px)',
        }}
      >
        <Field label="Ultima pesquisa:" value={formFields.dataUltimaConsulta} InputProps={{ readOnly: true }} disabled />
        <Field label="Retorno consulta:" value={formFields.retornoConsulta} InputProps={{ readOnly: true }} disabled />
      </Box>

      <IdentificacaoDevedor
        formFields={formFields}
        categoriasOptions={categoriasOptions}
        podeModificarFase={podeModificarFase}
        isEditMode={isEditMode}
        onChangeCampo={onChangeCampo}
        errors={{ ...errorFields, preposto: errorFields.nomePreposto }}
      />

      <Typography variant="h3" sx={{ mt: 3, mb: 1.5 }}>Devedores Antecedentes:</Typography>
      <Field
        label="Cód. Devedor principal:"
        name="codDevedorPrincipal"
        value={formFields.codDevedorPrincipal}
        onChange={onChangeCampo}
        disabled={!isEditMode}
        error={Boolean(erroCodDevedorPrincipal)}
        helperText={erroCodDevedorPrincipal}
        sx={{ mb: 2 }}
      />

      <TableDevedorGrupo content={devedoresDoGrupo} />

      <EnderecoDevedor
        enderecos={formFields.enderecos}
        estadosOptions={estadosOptions}
        podeModificarFase={podeModificarFase}
        isEditMode={isEditMode}
        onChangeEndereco={enderecosHandlers.onChangeItem}
        onAdicionarEndereco={enderecosHandlers.onAdicionarItem}
        onRemoverEndereco={enderecosHandlers.onRemoverItem}
        onMarcarPrincipal={enderecosHandlers.onMarcarPrincipal}
      />
      <TelefoneDevedor
        telefones={formFields.telefones}
        tiposTelefoneOptions={tiposTelefoneOptions}
        podeModificarFase={podeModificarFase}
        isEditMode={isEditMode}
        onChangeTelefone={telefonesHandlers.onChangeItem}
        onAdicionarTelefone={telefonesHandlers.onAdicionarItem}
        onRemoverTelefone={telefonesHandlers.onRemoverItem}
        onMarcarPrincipal={telefonesHandlers.onMarcarPrincipal}
      />
      <EmailDevedor
        emails={formFields.emails}
        podeModificarFase={podeModificarFase}
        isEditMode={isEditMode}
        onChangeEmail={emailsHandlers.onChangeItem}
        onAdicionarEmail={emailsHandlers.onAdicionarItem}
        onRemoverEmail={emailsHandlers.onRemoverItem}
        onMarcarPrincipal={emailsHandlers.onMarcarPrincipal}
      />

      <DadosAdvogado
        formFields={formFields}
        podeModificarFase={podeModificarFase}
        isEditMode={isEditMode}
        onChangeCampo={onChangeCampo}
        errors={errorFields}
      />

      <DadosSocios socios={formFields.socios} />

      <Dialog open={showSaveAlert} onClose={enviando ? undefined : onCancelarSalvar} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 4 }}>
          <DialogContentText sx={{ textAlign: 'center', fontSize: 18 }}>
            As alterações feitas no cadastro serão refletidas em
            <br />
            todos os Processos que fizerem referência a este
            <br />
            Devedor. Confirma as alterações?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ButtonOutlined onClick={onCancelarSalvar} disabled={enviando}>Cancelar</ButtonOutlined>
          <ButtonUI onClick={onConfirmarSalvar} disabled={enviando}>Confirmar</ButtonUI>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(erroSalvar)}
        autoHideDuration={4000}
        onClose={onFecharErroSalvar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={onFecharErroSalvar}>
          {erroSalvar}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(sucessoSalvar)}
        autoHideDuration={4000}
        onClose={onFecharSucessoSalvar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={onFecharSucessoSalvar}>
          {sucessoSalvar}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(erroWebService)}
        autoHideDuration={4000}
        onClose={onFecharErroWebService}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={onFecharErroWebService}>
          {erroWebService}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(sucessoWebService)}
        autoHideDuration={4000}
        onClose={onFecharSucessoWebService}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={onFecharSucessoWebService}>
          {sucessoWebService}
        </Alert>
      </Snackbar>
    </CardWrapper>
  );
}
AbaDevedor.propTypes = {
  dadosDevedor: PropTypes.object,
  podeModificarFase: PropTypes.bool,
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onDevedorMutado: PropTypes.func,
};
AbaDevedor.defaultProps = {
  dadosDevedor: null,
  podeModificarFase: false,
  idProcesso: null,
  onDevedorMutado: null,
};
